import React, { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
// Se você já tem schema/toProductPayload, mantém:
import { productFormSchema, toProductPayload } from "@/schema/product/product";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

// Ajuste o import conforme o seu projeto
import DualListSelect from "@/hooks/DualListSelect";

export default function ProductFormModal({
  open,
  onOpenChange,
  editingProduct,
  onSave,
  categories = [],
  ingredientsCatalog = [],
  productsCatalog = [],
}) {
  const defaultValues = {
    name: "",
    description: "",
    price: 0,
    category: "",
    image_url: "",
    available: true,

    // já existentes
    creatingFlag: false,
    unid: "",
    weight: 0,
    unid_product: "",
    weight_product: 0,
    totalIngredients: 0,
    totalProductsSelects: 0,

    min_stock: 0,

    // ingredientes (já existia)
    ingredientsSelectedIds: [],
    allIngredientsCatalog: [],

    // NOVOS CAMPOS (produtos extras)
    hasExtraProductsFlag: false, // checkbox da UI
    hasExtraIngredientsFlag: false, // checkbox da UI
    combo: false, // checkbox da UI
    productsSelectedCodes: [], // lista selecionada (ids de produtos)
    allProductsCatalog: [], // catálogo para mapear/filtrar
  };

  const {
    register,
    handleSubmit,
    control,
    reset,
    setValue,
    watch,
    setFocus,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(productFormSchema),
    defaultValues,
  });

  // Sempre que abrir e NÃO estiver editando, reseta o form
  useEffect(() => {
    if (open && !editingProduct) {
      reset(defaultValues);
    }
  }, [open, editingProduct, reset]);

  // Foco automático no campo nome quando abrir modal
  useEffect(() => {
    if (open) {
      try {
        setTimeout(() => setFocus("name"), 100);
      } catch (e) {
        // setFocus pode não existir em versões antigas - ignora
      }
    }
  }, [open, setFocus]);

  // injeta catálogos
  useEffect(() => {
    setValue("allIngredientsCatalog", ingredientsCatalog ?? []);
  }, [ingredientsCatalog, setValue]);

  useEffect(() => {
    setValue("allProductsCatalog", productsCatalog ?? []);
  }, [productsCatalog, setValue]);

  // preencher formulário quando entrar em modo edição
  useEffect(() => {
    if (!editingProduct) {
      reset({
        ...defaultValues,
        available: true,
        allIngredientsCatalog: ingredientsCatalog ?? [],
        allProductsCatalog: productsCatalog ?? [],
      });
      return;
    }

    const selectedIngredientIds = Array.isArray(editingProduct?.ingredients)
      ? editingProduct.ingredients.map((i) => String(i.id))
      : [];

    const selectedProductCodes = Array.isArray(editingProduct?.products)
      ? editingProduct.products
          .map((p) => p?.code)
          .filter(Boolean)
          .map(String)
      : [];

    reset({
      name: editingProduct?.name ?? "",
      description: editingProduct?.description ?? "",
      price: Number(editingProduct?.price ?? 0),
      category: editingProduct?.category ?? "",
      image_url: editingProduct?.image_url ?? "",
      available:
        typeof editingProduct?.available === "boolean"
          ? editingProduct.available
          : true,

      creatingFlag: (editingProduct?.creating ?? "N") === "S",
      unid: editingProduct?.unid ?? "",
      weight: Number(editingProduct?.weight ?? 0),
      unid_product: editingProduct?.unid_product ?? "",
      weight_product: Number(editingProduct?.weight_product ?? 0),
      totalIngredients: Number(editingProduct?.totalIngredients ?? 0),
      totalProductsSelects: Number(editingProduct?.totalProductsSelects ?? 0),

      min_stock: Number(editingProduct?.min_stock ?? 0),

      ingredientsSelectedIds: selectedIngredientIds,
      allIngredientsCatalog: ingredientsCatalog ?? [],

      // NOVOS:
      hasExtraProductsFlag: (editingProduct?.hasExtraProducts ?? "N") === "S",
      combo: (editingProduct?.combo ?? "N") === "S",
      hasExtraIngredientsFlag:
        (editingProduct?.hasExtraIngredients ?? "N") === "S",
      productsSelectedCodes: selectedProductCodes,
      allProductsCatalog: productsCatalog ?? [],
    });
  }, [editingProduct, reset, setValue, ingredientsCatalog, productsCatalog]);

  const creatingFlag = watch("creatingFlag");
  const hasExtraProductsFlag = watch("hasExtraProductsFlag");
  const hasExtraIngredientsFlag = watch("hasExtraIngredientsFlag");
  const combo = watch("combo");
  const imageUrl = watch("image_url");

  const onSubmit = async (values) => {
    // Aproveita o toProductPayload existente (seu schema atual),
    // e “extende” com os novos campos antes do POST/PUT.
    const payloadBase = toProductPayload
      ? toProductPayload(values)
      : { ...values };

    // NOVO: campos exigidos
    const payload = {
      ...payloadBase,
      hasExtraProducts: values.hasExtraProductsFlag ? "S" : "N",
      products: Array.isArray(values.productsSelectedCodes)
        ? values.productsSelectedCodes.map((code) => ({ code }))
        : [],
    };

    // Mapear creatingFlag para o campo 'creating' esperado pela API
    payload.creating = values.creatingFlag ? "S" : "N";

    // garantia defaults seguros caso schema não cubra tudo:
    payload.image_url = payload.image_url ?? "";
    payload.category = payload.category ?? "";
    payload.unid = payload.unid ?? "";
    payload.totalIngredients = Number(payload.totalIngredients ?? 0);
    payload.totalProductsSelects = Number(payload.totalProductsSelects ?? 0);
    payload.weight = Number(payload.weight ?? 0);
    payload.available = Boolean(payload.available);

    // Garantir que o campo 'combo' seja enviado como 'S' ou 'N'
    payload.combo = values.combo ? "S" : "N";

    await onSave?.(payload, editingProduct);
    onOpenChange(false);
    reset();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => onOpenChange(v)}>
      <DialogContent className="max-w-4xl w-full max-h-[90vh] overflow-y-auto rounded-2xl bg-white p-6">
        <DialogHeader>
          <DialogTitle>
            {editingProduct ? "Editar Produto" : "Novo Produto"}
          </DialogTitle>
          <DialogDescription>
            {editingProduct
              ? "Edite as informações do produto"
              : "Adicione um novo produto ao cardápio"}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 pt-2">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
            <div className="flex-1">
              <h3 className="text-lg font-semibold">Informações Básicas</h3>
              <p className="text-sm text-gray-500">
                Nome, descrição, preço e categoria.
              </p>
            </div>
            <div className="w-full md:w-80">
              <div className="text-sm text-right text-gray-600">
                {editingProduct ? "Modo Edição" : "Criar novo"}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2 space-y-4">
              {/* Nome */}
              <div>
                <Label htmlFor="name">Nome</Label>
                <Input id="name" {...register("name")} className="mt-1" />
                {errors.name && (
                  <p className="text-red-600 text-sm mt-1">
                    {errors.name.message}
                  </p>
                )}
              </div>

              {/* Descrição */}
              <div>
                <Label htmlFor="description">Descrição</Label>
                <Textarea
                  id="description"
                  rows={4}
                  {...register("description")}
                  className="mt-1"
                />
                {errors.description && (
                  <p className="text-red-600 text-sm mt-1">
                    {errors.description.message}
                  </p>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="price">Preço (R$)</Label>
                  <Input
                    id="price"
                    type="number"
                    step="0.01"
                    {...register("price")}
                    className="mt-1"
                  />
                  {errors.price && (
                    <p className="text-red-600 text-sm mt-1">
                      {errors.price.message}
                    </p>
                  )}
                </div>
                <div>
                  <Label htmlFor="min_stock">Quantidade Mínima</Label>
                  <Input
                    id="min_stock"
                    type="number"
                    min="0"
                    step="1"
                    {...register("min_stock")}
                    className="mt-1"
                  />
                  {errors.min_stock && (
                    <p className="text-red-600 text-sm mt-1">
                      {errors.min_stock.message}
                    </p>
                  )}
                </div>
              </div>

              <div>
                <Label htmlFor="category">Categoria</Label>
                <select
                  id="category"
                  className="w-full border rounded-md h-10 px-3 mt-1"
                  {...register("category")}
                >
                  <option value="">Selecione...</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
                {errors.category && (
                  <p className="text-red-600 text-sm mt-1">
                    {errors.category.message}
                  </p>
                )}
              </div>
            </div>

            {/* Coluna direita: imagem e status */}
            <aside className="space-y-4">
              <div>
                <Label htmlFor="image_url">URL da Imagem</Label>
                <Input
                  id="image_url"
                  placeholder="https://... ou /assets/arquivo.png"
                  {...register("image_url")}
                  className="mt-1"
                />
                {errors.image_url && (
                  <p className="text-red-600 text-sm mt-1">
                    {errors.image_url.message}
                  </p>
                )}
              </div>

              {/* Pré-visualização da imagem */}
              <div className="bg-gray-50 rounded-md p-2 border h-40 w-full flex items-center justify-center">
                {imageUrl ? (
                  <img
                    src={imageUrl}
                    alt="Preview"
                    className="max-h-full max-w-full object-contain rounded"
                  />
                ) : (
                  <div className="text-sm text-gray-400">
                    Preview da imagem aparecerá aqui
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <input
                    id="available"
                    type="checkbox"
                    {...register("available")}
                    className="h-4 w-4 rounded"
                  />
                  <Label htmlFor="available">Produto ativo</Label>
                </div>
                <p className="text-xs text-gray-500">
                  Marque se o produto estiver disponível para venda.
                </p>
              </div>
            </aside>
          </div>

          {/* Checkbox: Criar (ingredientes extras) */}
          <div className="flex items-center gap-3">
            <input
              id="creatingFlag"
              type="checkbox"
              {...register("creatingFlag")}
            />
            <Label htmlFor="creatingFlag">
              Usuario vai montar seu próprio produto?
            </Label>
          </div>
          {/* Campos adicionais quando criandoFlag é true */}

          {/* Checkbox: Criar (ingredientes extras) */}
          <div className="flex items-center gap-3">
            <input
              id="hasExtraIngredientsFlag"
              type="checkbox"
              {...register("hasExtraIngredientsFlag")}
            />
            <Label htmlFor="hasExtraIngredientsFlag">
              Incluir Ingredientes?
            </Label>
          </div>

          {/* DualListSelect de ingredientes (já existia) */}
          {hasExtraIngredientsFlag && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="totalIngredients">Total Ingredientes</Label>
                  <Input
                    id="totalIngredients"
                    type="number"
                    {...register("totalIngredients")}
                  />
                  {errors.totalIngredients && (
                    <p className="text-red-600 text-sm mt-1">
                      {errors.totalIngredients.message}
                    </p>
                  )}
                </div>
                <div>
                  <Label htmlFor="weight">Tamanho/Peso</Label>
                  <Input id="weight" type="number" {...register("weight")} />
                  {errors.weight && (
                    <p className="text-red-600 text-sm mt-1">
                      {errors.weight.message}
                    </p>
                  )}
                </div>
                <div>
                  <Label htmlFor="unid">Descrição</Label>
                  <Input
                    id="unid"
                    placeholder="ex.: ml, g, un"
                    {...register("unid")}
                  />
                  {errors.unid && (
                    <p className="text-red-600 text-sm mt-1">
                      {errors.unid.message}
                    </p>
                  )}
                </div>
              </div>

              <Controller
                control={control}
                name="ingredientsSelectedIds"
                render={({ field }) => (
                  <div>
                    <DualListSelect
                      options={ingredientsCatalog /* [{id,name,price}] */}
                      value={field.value /* array de IDs (string) */}
                      onChange={field.onChange}
                      titleLeft="Ingredientes Disponíveis"
                      titleRight="Ingredientes Selecionados"
                    />
                    {errors.ingredientsSelectedIds && (
                      <p className="text-red-600 text-sm mt-1">
                        {errors.ingredientsSelectedIds.message}
                      </p>
                    )}
                  </div>
                )}
              />
            </>
          )}

          {/* NOVO: Flag de Produtos Extras */}
          <div className="flex items-center gap-3 pt-2">
            <input
              id="hasExtraProductsFlag"
              type="checkbox"
              {...register("hasExtraProductsFlag")}
            />
            <Label htmlFor="hasExtraProductsFlag">Produtos extras?</Label>
          </div>

          {/* NOVO: DualListSelect de PRODUTOS extras (apenas quando marcado) */}
          {hasExtraProductsFlag && (
            <Controller
              control={control}
              name="productsSelectedCodes"
              render={({ field }) => (
                <div>
                  <DualListSelect
                    // options: converte productsCatalog em {id,name}
                    options={(productsCatalog ?? []).map((p) => ({
                      id: String(p.id ?? p.code ?? ""), // id que vamos enviar como "code"
                      name: String(p.name ?? p.title ?? p.id ?? ""),
                    }))}
                    value={field.value /* array de codes (string) */}
                    onChange={field.onChange}
                    titleLeft="Produtos Disponíveis"
                    titleRight="Produtos Selecionados"
                  />
                  {errors.productsSelectedCodes && (
                    <p className="text-red-600 text-sm mt-1">
                      {errors.productsSelectedCodes.message}
                    </p>
                  )}
                </div>
              )}
            />
          )}

          {hasExtraProductsFlag && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="totalProductsSelects">
                  Total produtos permitidos
                </Label>
                <Input
                  id="totalProductsSelects"
                  type="number"
                  {...register("totalProductsSelects")}
                />
                {errors.totalProductsSelects && (
                  <p className="text-red-600 text-sm mt-1">
                    {errors.totalProductsSelects.message}
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="weight">Tamanho/Peso</Label>
                <Input
                  id="weight"
                  type="number"
                  {...register("weight_product")}
                />
                {errors.weight_product && (
                  <p className="text-red-600 text-sm mt-1">
                    {errors.weight_product.message}
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="unid">Descrição</Label>
                <Input
                  id="unid"
                  placeholder="ex.: ml, g, un"
                  {...register("unid_product")}
                />
                {errors.unid_product && (
                  <p className="text-red-600 text-sm mt-1">
                    {errors.unid_product.message}
                  </p>
                )}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
            <div className="flex items-center gap-1 pt-2">
              <input id="combo" type="checkbox" {...register("combo")} />
              <Label htmlFor="combo">
                Permitir entrar como extra no combo?
              </Label>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {editingProduct ? "Atualizar Produto" : "Inserir Produto"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
